import {
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  SelectChangeEvent,
  SxProps,
} from '@mui/material';
import { Theme } from 'klayowebshared';
import React, { ChangeEvent, Component } from 'react';
import { withLocale, WithLocaleProps } from '../providers/LocaleProvider';
import SelectedIcon from '../resources/images/icons-check.svg';
import DropIcon from '../resources/images/icons-chevron-double.svg';
import { BaseSelectMenuOption } from '../types/components/selector.types';
import { TextField } from './TextField';

interface SelectMenuProps<
  T extends BaseSelectMenuOption = BaseSelectMenuOption,
> extends WithLocaleProps {
  noSelectionError?: boolean;
  groups?: string[] | null;
  highlightSelection?: boolean;
  isAutoFocus?: boolean;
  disabled?: boolean;
  dense?: boolean;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<T>
  ) => void;
  firstValueDefault?: boolean;
  getItemLabel?: (filter: T) => string;
  clearable?: boolean;
  disableItem?: (filter: T) => boolean;
  value?: T | null;
  defaultValue?: T | null;
  placeholder?: string;
  CustomHelperText?: string;
  label?: string | null;
  items?: [any, T][];
  menuClassName?: string;
  sx?: SxProps;
  className?: string;
}

interface SelectMenuState {
  isTouched: boolean;
}

class SelectMenuBase<T extends BaseSelectMenuOption = BaseSelectMenuOption> extends Component<
  SelectMenuProps<T>,
  SelectMenuState
> {
  static defaultProps = {
    noSelectionError: false,
    groups: null,
    highlightSelection: true,
  };

  ref: React.RefObject<HTMLDivElement | null>;

  constructor(props: SelectMenuProps<T>) {
    super(props);

    this.state = {
      isTouched: false,
    };

    this.ref = React.createRef();
  }

  componentDidMount() {
    const { isAutoFocus } = this.props;
    if (isAutoFocus) this.setState({ isTouched: true });
  }

  onChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<T>) {
    const { onChange } = this.props;
    if (e.target.value === undefined) return;

    if (onChange) onChange(e);

    this.setState({ isTouched: true });
  }

  onBlur(e: any) {
    this.setState({ isTouched: true });
  }

  renderGroup(group: any) {
    const { items } = this.props;
    return [
      <ListSubheader
        key={Math.random().toString(36).substring(7)}
        className='klayo-selectmenu_group'
      >
        {group}
      </ListSubheader>,
      items
        ?.filter(([key, filter]: any) => filter.group === group)
        .map(([key, filter]: any) => this.renderItem(key, filter)),
    ];
  }

  getHelperText() {
    const { CustomHelperText, noSelectionError, value, translate } = this.props;
    const { isTouched } = this.state;
    let textContent = '';

    if (noSelectionError && value === null && isTouched) {
      textContent = CustomHelperText
        ? CustomHelperText
        : translate
          ? translate('placeholders.pleaseSelectOption')
          : 'Please select an option';
    }

    return textContent;
  }

  renderItem(key: any, filter: any) {
    const { dense, getItemLabel, disableItem, highlightSelection } = this.props;

    return (
      <MenuItem
        className={'klayo-selectmenu_item ' + (dense ? 'klayo-selectmenu_item-dense ' : '')}
        key={key}
        disabled={disableItem ? disableItem(filter) : false}
        value={filter}
      >
        <ListItemText>{getItemLabel ? getItemLabel(filter) : filter.label}</ListItemText>
        {highlightSelection && (
          <ListItemIcon>
            <SelectedIcon />
          </ListItemIcon>
        )}
      </MenuItem>
    );
  }

  render() {
    const {
      label,
      items,
      placeholder,
      disabled,
      className,
      menuClassName,
      sx,
      dense,
      onChange,
      firstValueDefault,
      getItemLabel,
      clearable,
      disableItem,
      noSelectionError,
      groups,
    } = this.props;
    const { isTouched } = this.state;
    let { value } = this.props;

    const defaultValue = this.props.defaultValue
      ? this.props.defaultValue
      : firstValueDefault && items
        ? items[0][1]
        : null;
    if (!value) value = defaultValue;

    const helperText = this.getHelperText();

    return (
      <TextField<T>
        className={
          'klayo-selectmenu' +
          (dense ? ' klayo-selectmenu-dense' : '') +
          (className ? ' ' + className : '') +
          (groups ? ' klayo-selectmenu-hasgroups' : '')
        }
        select
        label={label}
        dense={dense}
        value={value}
        error={noSelectionError && value === null}
        helperText={helperText}
        clearable={clearable && !disabled}
        defaultValue={defaultValue}
        placeholder={placeholder}
        disabled={disabled}
        onChange={this.onChange.bind(this)}
        onBlur={this.onBlur.bind(this)}
        SelectProps={{
          MenuProps: {
            classes: {
              list:
                '.MuiMenu-list' +
                (menuClassName ? ` ${menuClassName} ` : '') +
                (groups ? ' klayo-selectmenu_list-hasgroups' : '') +
                (dense ? ' klayo-selectmenu_list-dense' : ''),
            },
          },
          IconComponent: () => <DropIcon className='klayo-selectmenu_dropbutton' />,
          autoWidth: false,
          displayEmpty: true,
          renderValue: (value: any) => (
            <div className='klayo-selectmenu_value'>
              {value !== '' ? (
                getItemLabel ? (
                  getItemLabel(value)
                ) : (
                  value.label
                )
              ) : placeholder ? (
                <div className='klayo-selectmenu_placeholder'>{placeholder}</div>
              ) : (
                ''
              )}
            </div>
          ),
        }}
        sx={{
          ...sx,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: Theme.getStyleVar('--neon-blue-nb-200'),
            },
            '&:hover fieldset': {
              borderColor: Theme.getStyleVar('--neon-blue-nb-300'),
            },
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
              borderColor: Theme.getStyleVar('--neon-blue-nb-500'),
            },
          },
        }}
      >
        {groups && groups.map((g: any) => this.renderGroup(g))}

        {!groups && items && items.map(([key, filter]: any) => this.renderItem(key, filter))}
      </TextField>
    );
  }
}

export const SelectMenu = withLocale(SelectMenuBase) as <
  T extends BaseSelectMenuOption = BaseSelectMenuOption,
>(
  props: Omit<SelectMenuProps<T>, keyof WithLocaleProps> & React.RefAttributes<SelectMenuBase<T>>
) => React.ReactElement;
